import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import TopBar from '../layout/TopBar';
import StatusBadge from '../components/StatusBadge';
import Typewriter from '../components/Typewriter';
import { useHGS } from '../context/HGSContext';
import { computeResidualScore, ragFor, bandFor, FRAUD_TRIGGER_IDS, PCC_MANDATORY_IDS } from '../data/riskUniverse';
import {
  ShieldAlert, Sparkles, Bot, BookOpen, FileText, BarChart3, Lightbulb, Loader2,
  ChevronRight, X, Flag, Filter, CheckCircle2, AlertTriangle, Zap, RefreshCw, Lock,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const RAG_STYLES = {
  Red: 'bg-red-500 text-white border-red-600',
  Amber: 'bg-amber-400 text-amber-950 border-amber-500',
  Yellow: 'bg-yellow-200 text-yellow-900 border-yellow-300',
  Green: 'bg-emerald-100 text-emerald-900 border-emerald-200',
};

const COPILOT_BUTTONS = [
  { key: 'explain', label: 'Explain', icon: BookOpen, color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
  { key: 'examples', label: 'Examples', icon: FileText, color: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100' },
  { key: 'score', label: 'Suggest score', icon: BarChart3, color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' },
  { key: 'mitigation', label: 'Mitigation', icon: Lightbulb, color: 'bg-red-50 text-[#CC0000] border-red-200 hover:bg-red-100' },
];

function riskToCopilotRow(r) {
  const { inherent, residual } = computeResidualScore(r);
  return {
    riskId: r.id,
    riskTitle: r.description,
    majorClassification: r.category,
    minorClassification: r.category,
    applicableFunction: r.category,
    ownerRole: r.ownerRole || 'Operating Head',
    currentControlLevel: ['Weak', 'Weak', 'Basic', 'Partial', 'Robust', 'Best-in-class'][r.controlMeasure] || 'Partial',
    frequency: ['', 'Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'][r.frequency] || 'Possible',
    businessImpact: ['', 'Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic'][r.impact] || 'Major',
    aiGuidanceExamples: r.description,
    mitigationPlan: 'Strengthen controls, assign owner, set review cadence.',
    targetDate: '2025-12-31',
    inherent, residual,
  };
}

function RiskRow({ r, onClick, selected }) {
  const { inherent, residual } = computeResidualScore(r);
  const rag = ragFor(residual);
  const band = bandFor(residual);
  const isFraudTrigger = FRAUD_TRIGGER_IDS.includes(r.id);
  const isPCCMandatory = PCC_MANDATORY_IDS.includes(r.id);
  const isHigh = rag === 'Red' || rag === 'Amber';

  return (
    <button
      onClick={() => onClick(r.id)}
      data-testid={`risk-row-${r.id}`}
      className={cn(
        'group text-left w-full grid grid-cols-[60px,90px,1fr,80px,80px,80px,90px] gap-3 items-center px-4 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition',
        selected && 'bg-[#CC0000]/5 border-l-4 border-l-[#CC0000]',
        isHigh && !selected && 'bg-red-50/30',
      )}
    >
      <span className="text-xs font-mono font-bold text-slate-800">{r.id}</span>
      <span className="text-[10px] font-semibold text-slate-600 uppercase truncate">{r.category}</span>
      <span className="text-sm text-slate-700 truncate">{r.description}</span>
      <span className="text-xs font-mono text-slate-500 tabular-nums text-center">{inherent}</span>
      <span className="text-xs font-bold text-slate-900 tabular-nums text-center">{residual}</span>
      <span className={cn('inline-flex items-center justify-center px-2 py-0.5 rounded border text-[10px] font-bold w-fit justify-self-center', RAG_STYLES[rag])}>{rag}</span>
      <div className="flex items-center gap-1 justify-end">
        {r.confirmed
          ? <span title="Confirmed by Operating Head" className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[9px] font-bold uppercase" data-testid={`confirmed-pill-${r.id}`}>
              <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />OK
            </span>
          : <span title="Pending confirmation" className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold uppercase" data-testid={`pending-pill-${r.id}`}>
              ●
            </span>
        }
        {isFraudTrigger && (
          <span title="Financial trigger for Fraud module" className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 text-[9px] font-bold uppercase">FRD</span>
        )}
        {isPCCMandatory && (
          <span title="PCC-mandatory risk" className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 text-[9px] font-bold uppercase">PCC</span>
        )}
        {band === 'High' && <Flag className="w-3 h-3 text-red-600" />}
      </div>
    </button>
  );
}

export default function RiskUniversePage() {
  const { riskUniverse, updateRisk, confirmAllRisks, user, canEditRiskAssessment, riskPublished, publishRiskAssessment,
          applicability, toggleApplicability, getEvaluation, setEvaluation, evaluationsForRisk } = useHGS();
  const [onlyMyAssigned, setOnlyMyAssigned] = useState(user?.role === 'operating_head');
  const [selectedId, setSelectedId] = useState(riskUniverse[0]?.id || 'R01');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterRag, setFilterRag] = useState('All');

  const isOperatingHead = canEditRiskAssessment; // only OH can edit/publish
  const isAdmin = user?.role === 'admin';

  // Co-Pilot state
  const [activeCopilot, setActiveCopilot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [llmData, setLlmData] = useState(null);
  const [llmError, setLlmError] = useState(null);
  const [llmCache, setLlmCache] = useState({});
  const [fromCache, setFromCache] = useState(false);

  const enriched = useMemo(() => riskUniverse.map((r) => {
    const { inherent, residual } = computeResidualScore(r);
    return { ...r, inherent, residual, rag: ragFor(residual), band: bandFor(residual) };
  }), [riskUniverse]);

  const categories = useMemo(() => ['All', ...Array.from(new Set(enriched.map((r) => r.category)))], [enriched]);

  const filtered = useMemo(() => enriched.filter((r) => {
    if (filterCategory !== 'All' && r.category !== filterCategory) return false;
    if (filterRag !== 'All' && r.rag !== filterRag) return false;
    if (onlyMyAssigned && user?.role === 'operating_head' && user.operatingHead) {
      return (r.assignedOhs || []).includes(user.operatingHead);
    }
    return true;
  }), [enriched, filterCategory, filterRag, onlyMyAssigned, user]);

  const selected = enriched.find((r) => r.id === selectedId) || enriched[0];

  const stats = useMemo(() => {
    const counts = { Red: 0, Amber: 0, Yellow: 0, Green: 0 };
    enriched.forEach((r) => { counts[r.rag] = (counts[r.rag] || 0) + 1; });
    const fraudTriggered = enriched.some((r) => FRAUD_TRIGGER_IDS.includes(r.id) && (r.band === 'Medium' || r.band === 'High'));
    const highCount = enriched.filter((r) => r.band === 'High').length;
    const classifications = new Set(enriched.map((r) => r.category)).size;
    const businessUnits = new Set(enriched.flatMap((r) => r.businessUnits || [])).size;
    const confirmedCount = enriched.filter((r) => r.confirmed).length;
    const sourceFile = enriched[0]?._sourceFile;
    const sourceSheet = enriched[0]?._sourceSheet;
    return { counts, fraudTriggered, highCount, total: enriched.length, classifications, businessUnits, confirmedCount, sourceFile, sourceSheet };
  }, [enriched]);

  // Confirmation gate for publishing.
  const allConfirmed = stats.confirmedCount === stats.total && stats.total > 0;

  const handlePublish = () => {
    const result = publishRiskAssessment();
    if (result && result.ok === false) {
      toast.error('Publish blocked', { description: result.error });
    } else if (result?.version) {
      toast.success('Risk Universe published', { description: `Version ${result.version} live. PCC / Fraud / DLC tasks active.` });
    }
  };

  const handleConfirmAll = () => {
    confirmAllRisks();
    toast.success('All 45 risks confirmed', { description: 'Ready to publish.' });
  };

  const runCopilot = async (key) => {
    if (!selected) return;
    const cacheKey = `${key}:${selected.id}`;
    if (llmCache[cacheKey]) {
      setActiveCopilot(key);
      setLlmData(llmCache[cacheKey]);
      setFromCache(true);
      setLlmError(null);
      return;
    }
    setLoading(true);
    setFromCache(false);
    setLlmError(null);
    setActiveCopilot(null);
    try {
      const riskRow = riskToCopilotRow(selected);
      const peerRows = enriched.filter((r) => r.category === selected.category && r.id !== selected.id).slice(0, 5).map(riskToCopilotRow);
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/copilot/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: key, riskRow, peerRows, moduleVersion: 'v3.2-universe' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLlmData(data);
      setLlmCache((c) => ({ ...c, [cacheKey]: data }));
      setActiveCopilot(key);
    } catch (e) {
      setLlmError(e?.message || 'LLM unavailable');
      setActiveCopilot(key);
    } finally {
      setLoading(false);
    }
  };

  const regenerate = () => {
    const cacheKey = `${activeCopilot}:${selected.id}`;
    setLlmCache((c) => { const n = { ...c }; delete n[cacheKey]; return n; });
    runCopilot(activeCopilot);
  };

  const adjustField = (field, delta, min, max) => {
    const cur = selected[field] || 0;
    const next = Math.max(min, Math.min(max, cur + delta));
    updateRisk(selected.id, { [field]: next });
  };

  return (
    <div>
      <TopBar
        title="Risk Universe Setup"
        subtitle="Step 1 of 5 · 45 risks sourced 1:1 from the official HMSI Risk Master · 16 classifications · RAG auto-calculated"
        breadcrumbs={['HGS Architecture', 'Risk Universe']}
      />

      <div className="p-8 space-y-5">
        {/* Architecture progress */}
        <ArchitectureProgress step={1} />

        {/* Ownership banner */}
        <div
          data-testid="risk-ownership-banner"
          className={cn(
            'flex items-center gap-4 p-4 rounded-xl border',
            isOperatingHead
              ? 'bg-gradient-to-r from-red-50/60 via-white to-white border-red-200'
              : 'bg-amber-50 border-amber-200'
          )}
        >
          <div className={cn(
            'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
            isOperatingHead ? 'bg-[#CC0000] text-white' : 'bg-amber-100 text-amber-700'
          )}>
            {isOperatingHead ? <ShieldAlert className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-slate-900">
              Risk Assessment is owned by Operating Head
              {isOperatingHead && <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#CC0000] text-white text-[9px] font-bold uppercase tracking-wider">You</span>}
            </div>
            <p className="text-xs text-slate-600 mt-0.5">
              {isOperatingHead
                ? 'You own risk scoring, control adjustment and publication. Once published, PCC / Fraud / DLC tasks become active for departments.'
                : `Risk inputs (frequency, impact, control) and scores are read-only for ${user?.role?.replace(/_/g, ' ')}. ${isAdmin ? 'Admin override is disabled in demo mode.' : 'Contact the Operating Head if a risk needs revision.'}`}
            </p>
          </div>
          {riskPublished?.published ? (
            <div className="flex-shrink-0 text-right">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wider" data-testid="risk-published-badge">
                <CheckCircle2 className="w-3 h-3" /> Published · {riskPublished.version}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                by {riskPublished.publishedBy} · {new Date(riskPublished.publishedAt).toLocaleDateString()}
              </div>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider" data-testid="risk-unpublished-badge">
              <AlertTriangle className="w-3 h-3" /> Not published · tasks paused
            </div>
          )}
          {isOperatingHead && (
            <div className="flex items-center gap-2">
              {!allConfirmed && (
                <button
                  onClick={handleConfirmAll}
                  data-testid="confirm-all-risks-btn"
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-emerald-50 border border-emerald-300 text-emerald-700 text-xs font-bold rounded-lg transition"
                  title="Confirm all 45 risk ratings in one click"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Confirm all ({stats.confirmedCount}/{stats.total})
                </button>
              )}
              <button
                onClick={handlePublish}
                data-testid="publish-risk-btn"
                disabled={!allConfirmed}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-xs font-bold rounded-lg shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#CC0000]"
                title={allConfirmed ? '' : `Confirm all ${stats.total} risks before publishing`}
              >
                <Zap className="w-3.5 h-3.5" />
                {riskPublished?.published ? 'Republish' : 'Publish Risk Universe'}
              </button>
            </div>
          )}
        </div>

        {/* Source banner — proves Risk Universe is sourced from HMSI's
            official Excel master, not auto-generated. */}
        <div
          data-testid="risk-source-banner"
          className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white border border-slate-700 shadow-sm"
        >
          <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-400">
              Source: Official HMSI Risk Master
            </div>
            <div className="text-sm font-mono mt-0.5 truncate" title={stats.sourceFile}>
              {stats.sourceFile} <span className="text-slate-400">·</span> Sheet: <span className="font-bold text-white">{stats.sourceSheet}</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-5 text-right">
            <SourceTile label="Risks" value={stats.total} />
            <SourceTile label="Classifications" value={stats.classifications} />
            <SourceTile label="Business Units" value={stats.businessUnits} />
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatTile label="Total Risks" value={stats.total} icon={ShieldAlert} tone="slate" />
          <StatTile label="Confirmed" value={`${stats.confirmedCount}/${stats.total}`} icon={CheckCircle2} tone={allConfirmed ? 'emerald' : 'amber'} data-testid="stat-confirmed" />
          <StatTile label="Red" value={stats.counts.Red} tone="red" />
          <StatTile label="Amber" value={stats.counts.Amber} tone="amber" />
          <StatTile label="Yellow" value={stats.counts.Yellow} tone="yellow" />
          <StatTile label="Green" value={stats.counts.Green} tone="emerald" />
        </div>

        {/* Fraud trigger alert */}
        {stats.fraudTriggered && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-sky-50 border border-sky-200" data-testid="fraud-trigger-alert">
            <AlertTriangle className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-bold text-sky-900">Fraud Prevention Checklist is now triggered</div>
              <p className="text-sky-700 text-xs mt-1">
                One or more financial risks (R23–R27, R30) are rated Medium or High. Proceed to Step 2 · Fraud Prevention to complete the controls checklist.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[1fr,420px] gap-5">
          {/* LEFT — Risk table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Risk Register</h3>
                <p className="text-[11px] text-slate-500">Click any row to open the AI Co-Pilot and adjust controls</p>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-slate-400" />
                <select
                  data-testid="filter-category"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
                >
                  {categories.map((c) => <option key={c}>{c}</option>)}
                </select>
                <select
                  data-testid="filter-rag"
                  value={filterRag}
                  onChange={(e) => setFilterRag(e.target.value)}
                  className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
                >
                  {['All', 'Red', 'Amber', 'Yellow', 'Green'].map((c) => <option key={c}>{c}</option>)}
                </select>
                {user?.role === 'operating_head' && (
                  <button
                    onClick={() => setOnlyMyAssigned((v) => !v)}
                    data-testid="my-assigned-toggle"
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1.5 rounded-md border transition ${onlyMyAssigned ? 'bg-[#CC0000] text-white border-[#CC0000]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                    title="Toggle between My Assigned Risks and All Risks"
                  >
                    {onlyMyAssigned ? '✓ My Assigned' : 'All Risks'}
                  </button>
                )}
              </div>
            </div>

            {/* Header */}
            <div className="grid grid-cols-[60px,90px,1fr,80px,80px,80px,90px] gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              <span>ID</span>
              <span>Category</span>
              <span>Description</span>
              <span className="text-center">Inherent</span>
              <span className="text-center">Residual</span>
              <span className="text-center">RAG</span>
              <span className="text-right">Flags</span>
            </div>

            <div className="max-h-[560px] overflow-y-auto" data-testid="risk-register-list">
              {filtered.map((r) => (
                <RiskRow key={r.id} r={r} onClick={setSelectedId} selected={selectedId === r.id} />
              ))}
              {filtered.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-500">No risks match the filters.</div>
              )}
            </div>
          </div>

          {/* RIGHT — Selected risk + Co-Pilot */}
          <div className="space-y-4">
            {/* Selected risk control panel */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="risk-detail-panel">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{selected?.category}</div>
                  <div className="text-sm font-bold text-slate-900 font-mono">{selected?.id}</div>
                </div>
                <span className={cn('px-2 py-1 rounded border text-xs font-bold', RAG_STYLES[selected?.rag])}>{selected?.rag}</span>
              </div>
              {/* ─── Section 1: MASTER RISK (read-only for all roles) ─── */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-3" data-testid="master-risk-section">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Lock className="w-3 h-3 text-slate-500" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-slate-600">Master Risk · Risk Management Secretariat</span>
                </div>
                <p className="text-sm text-slate-800 leading-relaxed font-semibold">{selected?.description}</p>
                {selected?._sourceRow != null && (
                  <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[9px] font-medium text-slate-600">
                    <span>Source: <b className="font-bold text-slate-700">Official HMSI Risk Master</b></span>
                    <span className="text-slate-400">·</span>
                    <span className="font-mono">Row {selected._sourceRow}</span>
                  </div>
                )}
              </div>

              {/* ─── Section 2: APPLICABILITY MAPPING (Admin-editable) ─── */}
              {selected && (
                <ApplicabilitySection
                  risk={selected}
                  applicability={applicability}
                  toggleApplicability={toggleApplicability}
                  isAdmin={user?.role === 'admin'}
                />
              )}

              {/* ─── Section 3: OPERATIONAL EVALUATION ─── */}
              {selected && (
                <OperationalEvaluation
                  risk={selected}
                  user={user}
                  getEvaluation={getEvaluation}
                  setEvaluation={setEvaluation}
                  evaluationsForRisk={evaluationsForRisk}
                  updateRisk={updateRisk}
                  isOperatingHead={isOperatingHead}
                />
              )}
            </div>

            {/* AI Co-Pilot — Operating Head only */}
            {isOperatingHead ? (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="copilot-panel">
              <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-slate-900 to-[#1a0000] text-white">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#CC0000] to-[#990000] flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-bold">AI Risk Co-Pilot</div>
                    <div className="text-[10px] text-slate-400">Claude Sonnet 4.5 · grounded in the 45-risk universe</div>
                  </div>
                </div>
              </div>
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-[10px] text-amber-900 flex items-center gap-1.5" data-testid="copilot-disclaimer">
                <Sparkles className="w-3 h-3 flex-shrink-0" />
                <span>AI supports decision-making; final judgment remains with the user.</span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {COPILOT_BUTTONS.map((b) => (
                    <button
                      key={b.key}
                      onClick={() => runCopilot(b.key)}
                      disabled={loading}
                      data-testid={`copilot-${b.key}`}
                      className={cn('inline-flex items-center gap-1.5 px-2.5 py-2 text-xs font-semibold border rounded-lg transition disabled:opacity-60', b.color)}
                    >
                      <b.icon className="w-3.5 h-3.5" />
                      {b.label}
                    </button>
                  ))}
                </div>

                <CoPilotOutput
                  activeKey={activeCopilot}
                  loading={loading}
                  data={llmData}
                  llmError={llmError}
                  fromCache={fromCache}
                  onClose={() => { setActiveCopilot(null); setLlmData(null); }}
                  onRegenerate={regenerate}
                  selected={selected}
                />
              </div>
            </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-center" data-testid="copilot-locked">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mx-auto mb-2">
                  <Lock className="w-4 h-4" />
                </div>
                <div className="text-xs font-bold text-slate-800">AI Risk Co-Pilot is available to Operating Head only</div>
                <p className="text-[11px] text-slate-500 mt-1 max-w-xs mx-auto">
                  AI scoring suggestions, mitigation and explanation are part of the risk-ownership workflow.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function SourceTile({ label, value }) {
  return (
    <div className="text-right" data-testid={`risk-source-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-lg font-bold tabular-nums text-white">{value}</div>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tone = 'slate' }) {
  const toneMap = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    red: 'bg-red-50 text-red-800 border-red-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    yellow: 'bg-yellow-50 text-yellow-900 border-yellow-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  };
  return (
    <div className={cn('rounded-xl border p-4', toneMap[tone])}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-60" />}
      </div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function Stepper({ label, value, onInc, onDec, disabled }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-[10px] font-bold text-slate-500 uppercase">{label}</span>
      <div className={cn('flex items-center border border-slate-200 rounded-lg overflow-hidden', disabled && 'opacity-50')}>
        <button onClick={onDec} disabled={disabled} className="w-7 h-7 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">−</button>
        <span className="w-8 text-center text-sm font-bold font-mono text-slate-900 tabular-nums">{value}</span>
        <button onClick={onInc} disabled={disabled} className="w-7 h-7 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:hover:bg-transparent">+</button>
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className={cn('rounded-lg p-2', accent && RAG_STYLES[accent])}>
      <div className="text-[9px] font-bold uppercase opacity-70">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

function CoPilotOutput({ activeKey, loading, data, llmError, fromCache, onClose, onRegenerate, selected }) {
  if (!activeKey && !loading) {
    return (
      <div className="text-center py-8 text-slate-400">
        <Bot className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        <p className="text-xs font-medium text-slate-500">Ask the Co-Pilot about {selected?.id}</p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-[#CC0000] mb-2" />
        <p className="text-xs font-medium text-slate-700">Analyzing {selected?.id}…</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
        {llmError || 'LLM unavailable'} — please retry.
      </div>
    );
  }

  const fromLLM = data._source && !data._fallback;
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] text-slate-500 flex items-center gap-1.5 flex-wrap">
          {fromLLM ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold text-[9px] uppercase">
              <Sparkles className="w-2.5 h-2.5" /> Claude 4.5
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold text-[9px] uppercase">Offline</span>
          )}
          {fromCache && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-bold text-[9px] uppercase">
              <Zap className="w-2.5 h-2.5" /> Cached
            </span>
          )}
          <span>· {selected?.id}</span>
        </div>
        <div className="flex items-center gap-1">
          {fromCache && fromLLM && (
            <button onClick={onRegenerate} className="inline-flex items-center gap-1 px-1.5 py-1 text-[10px] font-semibold text-slate-600 hover:text-[#CC0000] rounded hover:bg-slate-50">
              <RefreshCw className="w-3 h-3" />
            </button>
          )}
          <button onClick={onClose} className="w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-400">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {data.title && <div className="text-sm font-bold text-slate-900">{data.title}</div>}

      {activeKey === 'explain' && (
        <>
          {data.summary && (
            <p className="text-xs text-slate-700 leading-relaxed">
              <Typewriter text={data.summary} active={fromLLM && !fromCache} />
            </p>
          )}
          {data.drivers && (
            <ul className="space-y-1">
              {data.drivers.slice(0, 4).map((d, i) => (
                <li key={`${i}-${(d || '').slice(0, 24)}`} className="flex items-start gap-1.5 text-[11px] text-slate-700">
                  <ChevronRight className="w-3 h-3 text-[#CC0000] mt-0.5 flex-shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {activeKey === 'examples' && data.cases && (
        <div className="space-y-2">
          {data.cases.slice(0, 4).map((c) => (
            <div key={`${c.company || 'c'}-${c.year || ''}`} className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[11px] font-bold text-slate-900">{c.company}</span>
                <span className="text-[10px] text-slate-500 font-mono">{c.year}</span>
              </div>
              <p className="text-[11px] text-slate-700 leading-relaxed">{c.impact}</p>
            </div>
          ))}
        </div>
      )}

      {activeKey === 'score' && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 rounded bg-slate-50 border border-slate-200">
              <div className="text-[9px] font-bold text-slate-500 uppercase">Likelihood</div>
              <div className="text-xs font-bold text-slate-900">{data.likelihood}</div>
            </div>
            <div className="p-2 rounded bg-slate-50 border border-slate-200">
              <div className="text-[9px] font-bold text-slate-500 uppercase">Impact</div>
              <div className="text-xs font-bold text-slate-900">{data.impact}</div>
            </div>
            <div className="p-2 rounded bg-amber-50 border border-amber-200">
              <div className="text-[9px] font-bold text-amber-700 uppercase">Score</div>
              <div className="text-xs font-bold text-amber-900">{data.score} · {data.band}</div>
            </div>
          </div>
          {data.rationale && <p className="text-[11px] text-slate-700 leading-relaxed"><Typewriter text={data.rationale} active={fromLLM && !fromCache} /></p>}
        </div>
      )}

      {activeKey === 'mitigation' && data.actions && (
        <div className="space-y-1.5">
          {data.actions.slice(0, 4).map((a, i) => (
            <div key={`${a.owner || 'o'}-${a.due || ''}-${i}`} className="flex items-start gap-2 p-2 rounded-lg bg-red-50 border border-red-100">
              <div className="w-5 h-5 rounded bg-[#CC0000] text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-slate-900 leading-snug">{a.action}</p>
                <p className="text-[9px] text-slate-500 mt-0.5">{a.owner} · {a.due}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Architecture progress breadcrumb strip
export function ArchitectureProgress({ step }) {
  const STEPS = [
    { n: 1, label: 'Risk Universe', to: '/risk' },
    { n: 2, label: 'Fraud', to: '/fraud' },
    { n: 3, label: 'PCC', to: '/pcc' },
    { n: 4, label: 'DLC', to: '/dlc' },
    { n: 5, label: 'GRC Review', to: '/grc-review' },
  ];
  return (
    <div className="flex items-center gap-1 overflow-x-auto p-3 bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-xl" data-testid="architecture-progress">
      {STEPS.map((s, idx) => {
        const done = s.n < step;
        const active = s.n === step;
        return (
          <React.Fragment key={s.n}>
            <div className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0',
              active ? 'bg-[#CC0000] text-white shadow-sm' : done ? 'bg-emerald-100 text-emerald-800' : 'bg-white text-slate-500 border border-slate-200'
            )}>
              <span className={cn('w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold',
                active ? 'bg-white/20' : done ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
              )}>
                {done ? <CheckCircle2 className="w-3 h-3" /> : s.n}
              </span>
              <span className="whitespace-nowrap">{s.label}</span>
            </div>
            {idx < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Applicability Section — Admin maintains which Operating Heads each master
// risk applies to. Read-only chip-list for non-admins.
// ────────────────────────────────────────────────────────────────────────────
const OH_META = {
  'hr-admin':      { label: 'HR & Admin',    color: '#0EA5E9' },
  'manufacturing': { label: 'Manufacturing', color: '#CC0000' },
};

function ApplicabilitySection({ risk, applicability, toggleApplicability, isAdmin }) {
  const assigned = applicability[risk.id] || [];
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 mb-3" data-testid="applicability-section">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-slate-600">Applicability</span>
          <span className="text-[9px] text-slate-400">· {assigned.length} Operating Head(s)</span>
        </div>
        {!isAdmin && <span className="text-[9px] uppercase tracking-wider text-slate-400">Read-only</span>}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {Object.keys(OH_META).map((ohId) => {
          const isOn = assigned.includes(ohId);
          const meta = OH_META[ohId];
          return (
            <button
              key={ohId}
              type="button"
              disabled={!isAdmin}
              onClick={() => isAdmin && toggleApplicability(risk.id, ohId)}
              data-testid={`applicability-toggle-${risk.id}-${ohId}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold transition ${isOn ? 'text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-500'} ${!isAdmin ? 'cursor-default' : 'hover:opacity-90'}`}
              style={isOn ? { backgroundColor: meta.color } : undefined}
              title={isAdmin ? `Toggle applicability for ${meta.label}` : ''}
            >
              {isOn ? <CheckCircle2 className="w-2.5 h-2.5" /> : <span className="inline-block w-2.5 h-2.5 rounded-full border border-current opacity-60" />}
              {meta.label}
            </button>
          );
        })}
      </div>
      {risk.businessUnitsRaw && (
        <div className="text-[10px] text-slate-500 leading-snug">
          <span className="font-bold uppercase tracking-wider mr-1">Business Units:</span>
          {risk.businessUnitsRaw}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Operational Evaluation — the OH's contextualised risk evaluation. When the
// viewer is the assigned Operating Head, the form is editable. For other
// roles we render a stacked read-only summary of every OH's evaluation.
// ────────────────────────────────────────────────────────────────────────────
function OperationalEvaluation({ risk, user, getEvaluation, setEvaluation, evaluationsForRisk, isOperatingHead }) {
  const myOhId = user?.role === 'operating_head' ? user.operatingHead : null;
  const isAssignedOh = myOhId && (risk.assignedOhs || []).includes(myOhId);

  if (isOperatingHead && isAssignedOh) {
    // Editable form for the OH this risk is assigned to
    const ev = getEvaluation(risk.id, myOhId);
    const patch = (p) => setEvaluation(risk.id, myOhId, p);
    const { inherent, residual } = computeResidualScore(ev);
    const band = bandFor(residual);
    const rag = ragFor(residual);
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-3" data-testid="op-eval-edit">
        <div className="flex items-center gap-1.5 mb-2">
          <ShieldAlert className="w-3 h-3 text-emerald-700" />
          <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-emerald-800">Operational Evaluation — {OH_META[myOhId]?.label}</span>
        </div>

        <TextArea label="Concrete potential risk in your department" value={ev.concretePotentialRisk} onChange={(v) => patch({ concretePotentialRisk: v })} testid="eval-concrete" />
        <TextArea label="Current situation" value={ev.currentSituation} onChange={(v) => patch({ currentSituation: v })} testid="eval-current" />
        <TextArea label="Preventive measures" value={ev.preventiveMeasures} onChange={(v) => patch({ preventiveMeasures: v })} testid="eval-preventive" />
        <TextArea label="Recovery measures" value={ev.recoveryMeasures} onChange={(v) => patch({ recoveryMeasures: v })} testid="eval-recovery" />

        <div className="grid grid-cols-3 gap-3 mt-3 mb-3">
          <Stepper label="Freq"    value={ev.frequency}      onInc={() => patch({ frequency:      Math.min(5, ev.frequency + 1) })}      onDec={() => patch({ frequency:      Math.max(1, ev.frequency - 1) })} />
          <Stepper label="Impact"  value={ev.impact}         onInc={() => patch({ impact:         Math.min(5, ev.impact + 1) })}         onDec={() => patch({ impact:         Math.max(1, ev.impact - 1) })} />
          <Stepper label="Control" value={ev.controlMeasure} onInc={() => patch({ controlMeasure: Math.min(5, ev.controlMeasure + 1) })} onDec={() => patch({ controlMeasure: Math.max(1, ev.controlMeasure - 1) })} />
        </div>

        <div className="grid grid-cols-3 gap-2 text-center mb-3">
          <Metric label="Inherent" value={inherent} />
          <Metric label="Residual" value={residual} accent={rag} />
          <Metric label="Band" value={band} />
        </div>

        <button
          onClick={() => patch({ confirmed: !ev.confirmed })}
          data-testid="confirm-risk-btn"
          className={ev.confirmed
            ? "w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition"
            : "w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold hover:bg-emerald-100 transition"}
        >
          <CheckCircle2 className="w-4 h-4" />
          {ev.confirmed ? 'Evaluation confirmed · click to unconfirm' : 'Confirm this operational evaluation'}
        </button>
      </div>
    );
  }

  // Read-only stacked view of every assigned OH's evaluation
  const stack = evaluationsForRisk(risk.id);
  return (
    <div className="space-y-2" data-testid="op-eval-readonly">
      {stack.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] italic text-slate-500">
          No Operating Head has been assigned to this risk yet.
        </div>
      )}
      {stack.map(({ ohId, evaluation }) => {
        const { inherent, residual } = computeResidualScore(evaluation);
        return (
          <div key={ohId} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`op-eval-card-${ohId}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: OH_META[ohId]?.color }} />
                <span className="text-[10px] font-bold text-slate-700">{OH_META[ohId]?.label}</span>
                {evaluation.confirmed && <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" />}
              </div>
              <span className="text-[9px] text-slate-500 font-mono">F·{evaluation.frequency} I·{evaluation.impact} C·{evaluation.controlMeasure} → R·{residual}</span>
            </div>
            {evaluation.concretePotentialRisk
              ? <p className="text-[11px] text-slate-700 leading-snug">{evaluation.concretePotentialRisk}</p>
              : <p className="text-[10px] italic text-slate-400">Operational interpretation not yet provided.</p>}
          </div>
        );
      })}
    </div>
  );
}

function TextArea({ label, value, onChange, testid }) {
  return (
    <label className="block mb-2">
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-600 mb-0.5">{label}</div>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        rows={2}
        className="w-full px-2 py-1.5 text-[11px] border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-y"
        placeholder="—"
      />
    </label>
  );
}
